import { BoxGeometry, Mesh, Material, Object3D, Vector3 } from 'three';

/**
 * Class representing a box with a customizable origin.
 * 
 * This class extends the THREE.js BoxGeometry and Mesh classes
 * to allow for a customizable origin point based on a provided
 * alignment vector.
 * 
 * @example
 * // Create a box with a bottom-center origin
 * const box = AlignedBox.bottomCenter(1, 1, 1);
 * 
 * // Create a box with a top-center origin
 * const box = AlignedBox.topCenter(1, 1, 1);
 */
class AlignedBox {
  private mesh: Mesh;
  private geometry: BoxGeometry;

  private constructor(
    width: number,
    height: number,
    depth: number,
    material?: Material,
    originAlignment: Vector3 = new Vector3(0.5, 0.5, 0.5)
  ) {
    this.geometry = new BoxGeometry(width, height, depth);
    this.mesh = new Mesh(this.geometry, material);

    // Clamp the alignment vector to ensure its components are in the range [0, 1]
    originAlignment.clampScalar(0, 1);

    // Determine the offset for each axis
    const xOffset = (originAlignment.x - 0.5) * width;
    const yOffset = (originAlignment.y - 0.5) * height;
    const zOffset = (originAlignment.z - 0.5) * depth;

    // Shift the vertices to set the origin based on the alignment vector
    const positions = this.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      positions[i] += xOffset;     // x-coordinate
      positions[i + 1] += yOffset; // y-coordinate
      positions[i + 2] += zOffset; // z-coordinate
    }

    // Update the geometry to reflect the changes
    this.geometry.attributes.position.needsUpdate = true;
  }

  static bottomCenter(
    width: number,
    height: number,
    depth: number,
    material?: Material
  ): AlignedBox {
    return new AlignedBox(width, height, depth, material, new Vector3(0.5, 0, 0.5));
  }

  static topCenter(
    width: number,
    height: number,
    depth: number,
    material?: Material
  ): AlignedBox {
    return new AlignedBox(width, height, depth, material, new Vector3(0.5, 1, 0.5));
  }

  static center(
    width: number,
    height: number,
    depth: number,
    material?: Material
  ): AlignedBox {
    return new AlignedBox(width, height, depth, material, new Vector3(0.5, 0.5, 0.5));
  }

  static topLeftFront(
    width: number,
    height: number,
    depth: number,
    material?: Material
  ): AlignedBox {
    return new AlignedBox(width, height, depth, material, new Vector3(0, 1, 0));
  }

  // ... add more static methods for other alignments as needed

  /**
   * Adds this box to a parent Object3D (could be the scene or another object).
   * 
   * @param parent - The parent Object3D to which the box will be added.
   */
  addTo(parent: Object3D) {
    parent.add(this.mesh);
  }

  /**
   * Removes this box from a parent Object3D.
   * 
   * @param parent - The parent Object3D from which the box will be removed.
   */
  removeFrom(parent: Object3D) {
    parent.remove(this.mesh);
  }

  /**
   * Provides direct access to the underlying mesh for manipulation or querying.
   * 
   * @returns The mesh representing this box.
   */
  getMesh() {
    return this.mesh;
  }

  // Other functionalities you may need...
}

export default AlignedBox;
